/**
 * Client-safe board area model (forecast areas spec, section 10.2). The server builds a
 * `BoardAreaProps` value per board (`board-areas.ts`) and passes it to `LeaderboardTable`, which
 * never imports the catalogue itself. Everything here is pure.
 */
import type { AreaBoardSport } from "@/lib/areas/types";

/** One area chip. On the surf and piste boards it links to the area page; elsewhere it filters. */
export type BoardAreaChip = Readonly<{
  slug: string;
  name: string;
  countryCode: string;
  countryName: string;
  /** Members of this area on this board. */
  count: number;
  /** The area page (surf and piste boards only). */
  href: string | null;
  /**
   * A region-tier area whose child areas can also have chips. Its count includes its children,
   * so country totals skip it, and it sorts before the leaf chips of its country.
   */
  region?: boolean;
}>;

/**
 * The compact area index for one board's rows only: each row's leaf area, the parent of every
 * area that appears, and every such area's name. A filter for a region matches its children.
 */
export type BoardAreaIndex = Readonly<{
  spotAreas: Readonly<Record<string, string>>;
  areaParents: Readonly<Record<string, string>>;
  areaNames: Readonly<Record<string, string>>;
}>;

export type BoardOffSeason = Readonly<{
  /** "Most resorts are closed for the season. They usually open in December." */
  message: string;
  href: string;
  linkLabel: string;
}>;

export type BoardAreaProps = Readonly<{
  sport: AreaBoardSport;
  /** "links" on surf and piste (chips go to area pages), "filter" on every other board. */
  chipMode: "links" | "filter";
  chips: readonly BoardAreaChip[];
  /** Where "All areas" goes (the hub's Browse by area block) on the link boards. */
  allAreasHref: string | null;
  index: BoardAreaIndex;
  /** Whether the Today / This week toggle is offered (gate checked on the server). */
  weekAvailable: boolean;
  /** The piste week view offers "Group by area". */
  weekGroupByArea: boolean;
  offSeason: BoardOffSeason | null;
  /** Show the Near you chip (client-only, after hydration). */
  nearYou: boolean;
}>;

/** True when the spot's leaf area is the area or sits under it. */
export function spotInArea(index: BoardAreaIndex, spotSlug: string, areaSlug: string): boolean {
  return areaContains(index, index.spotAreas[spotSlug] ?? null, areaSlug);
}

/** True when `leafSlug` is `areaSlug` or one of its descendants. */
export function areaContains(index: BoardAreaIndex, leafSlug: string | null, areaSlug: string): boolean {
  const seen = new Set<string>();
  let current: string | null = leafSlug;
  while (current && !seen.has(current)) {
    if (current === areaSlug) return true;
    seen.add(current);
    current = index.areaParents[current] ?? null;
  }
  return false;
}

/** A filter value is usable only when the area is known to this board. */
export function knownBoardArea(index: BoardAreaIndex, areaSlug: string | null): string | null {
  if (!areaSlug) return null;
  return Object.hasOwn(index.areaNames, areaSlug) ? areaSlug : null;
}

/** Pure: keep entries whose spot sits in the area; returns the input when no area is set. */
export function filterByArea<T extends Readonly<{ slug: string }>>(
  index: BoardAreaIndex,
  entries: readonly T[],
  areaSlug: string | null,
): readonly T[] {
  if (!areaSlug) return entries;
  return entries.filter((entry) => spotInArea(index, entry.slug, areaSlug));
}

/** Chips grouped by country in their given order (the server orders them). */
export function chipsByCountry<T extends BoardAreaChip>(chips: readonly T[]): readonly Readonly<{
  countryCode: string;
  countryName: string;
  chips: readonly T[];
}>[] {
  const groups: { countryCode: string; countryName: string; chips: T[] }[] = [];
  for (const chip of chips) {
    const last = groups[groups.length - 1];
    if (last && last.countryCode === chip.countryCode) last.chips.push(chip);
    else groups.push({ countryCode: chip.countryCode, countryName: chip.countryName, chips: [chip] });
  }
  return groups;
}
